import { useState, useCallback, useRef, useEffect } from 'react'

/**
 * Hook for managing infinite scroll pagination with cursor-based loading
 * Simplified to work with server's perfect sequential pagination
 */
const useInfiniteScroll = (fetchLogs, { 
  loadThreshold = 150,        // Load more when within this many pixels of edge
  initialPageSize = 100       // Initial page size
}) => {
  const [logs, setLogs] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasMore, setHasMore] = useState({ before: true, after: true })
  const [totalCount, setTotalCount] = useState(0)
  
  // Track scroll position and auto-scroll behavior - start with auto-scroll enabled
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true)
  const scrollRef = useRef(null)
  const lastScrollTop = useRef(0)
  const isAdjustingScroll = useRef(false)
  
  /**
   * Initial load - gets most recent logs
   */
  const loadInitial = useCallback(async (options = {}) => {
    setIsLoading(true)
    try {
      const result = await fetchLogs({
        ...options,
        limit: initialPageSize,
        initialLoad: true
      })
      
      if (result.success) {
        
        // Server already provides logs in perfect sequential order - no sorting needed
        setLogs(result.logs)
        setHasMore({ 
          before: result.hasBefore !== false, 
          after: result.hasAfter !== false 
        })
        setTotalCount(result.totalCount || 0)
        setShouldAutoScroll(true)
        
        // Initial load completed
        
        // Auto-scroll to bottom after initial load - ensure DOM is updated
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (scrollRef.current) {
              const { scrollHeight, clientHeight } = scrollRef.current
              const targetScrollTop = Math.max(0, scrollHeight - clientHeight)
              scrollRef.current.scrollTop = targetScrollTop
              
              // Enable auto-scroll by default after initial load
              setShouldAutoScroll(true)
            }
          })
        })
        
        return result
      } else {
        console.error('Failed to load initial logs: result not successful', result)
        return result || { success: false, error: 'No result returned' }
      }
    } catch (error) {
      console.error('Failed to load initial logs:', error)
      return { success: false, error: error.message }
    } finally {
      setIsLoading(false)
    }
  }, [fetchLogs, initialPageSize])

  /**
   * Load older logs (scrolling up)
   */
  const loadOlderLogs = useCallback(async (options = {}) => {
    if (isLoading || !hasMore.before) {
      // Skipped loading older logs
      return
    }
    
    // Use the first (oldest) log currently available as cursor for fetching older logs
    const firstLog = logs[0]
    if (!firstLog) {
      // No local logs available to use as cursor
      return
    }
    
    
    setIsLoading(true)
    try {
      const result = await fetchLogs({
        ...options,
        cursor: { timestamp: firstLog.timestamp, sequenceId: firstLog.sequenceId },
        direction: 'before',
        limit: 50
      })
      
      // Older logs result received
      
      if (result.success && result.logs.length > 0) {
        // Preserve scroll position when adding items above
        const scrollContainer = scrollRef.current
        let prevScrollHeight = 0
        let prevScrollTop = 0
        
        if (scrollContainer) {
          prevScrollHeight = scrollContainer.scrollHeight
          prevScrollTop = scrollContainer.scrollTop
        }
        
        setLogs(prevLogs => {
          // Filter out any logs that already exist to prevent duplicates
          const existingIds = new Set(prevLogs.map(log => log.id))
          const newLogs = result.logs.filter(log => !existingIds.has(log.id))
          
          
          const updatedLogs = [...newLogs, ...prevLogs]
          
          // Server provides logs in correct order - just prepend older logs
          return updatedLogs
        })
        
        // Maintain scroll position after DOM update - use multiple attempts to ensure DOM is ready
        const preserveScrollPosition = (attempts = 0) => {
          if (!scrollContainer || prevScrollHeight === 0 || attempts > 10) {
            // Skipping scroll preservation
            return
          }
          
          const newScrollHeight = scrollContainer.scrollHeight
          const heightDifference = newScrollHeight - prevScrollHeight
          
          // Scroll preservation attempt
          
          // Only adjust if there's actually a height difference
          if (heightDifference > 0) {
            isAdjustingScroll.current = true
            const newScrollTop = prevScrollTop + heightDifference
            scrollContainer.scrollTop = newScrollTop
            lastScrollTop.current = newScrollTop // Update the reference to avoid confusion
            // Adjusted scroll position
            
            // Re-enable scroll handling after a brief delay
            setTimeout(() => {
              isAdjustingScroll.current = false
            }, 50)
          } else if (heightDifference === 0 && attempts < 5) {
            // DOM might not be updated yet, try again
            // No height change yet, retrying
            setTimeout(() => preserveScrollPosition(attempts + 1), 10)
          } else {
            // No scroll adjustment needed
          }
        }
        
        // Use requestAnimationFrame for better timing with DOM updates
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            preserveScrollPosition()
          })
        })
        
        setHasMore(prev => ({ 
          ...prev, 
          before: result.hasBefore !== false 
        }))
        
      } else if (result.success && result.logs.length === 0) {
        // No older logs available
        setHasMore(prev => ({ ...prev, before: false }))
      }
    } catch (error) {
      console.error('Failed to load older logs:', error)
    } finally {
      setIsLoading(false)
    }
  }, [fetchLogs, isLoading, hasMore.before, logs])

  /**
   * Load newer logs (scrolling down or new realtime events)
   */
  const loadNewerLogs = useCallback(async (options = {}) => {
    if (isLoading || !hasMore.after) return
    
    // Use the last (newest) log currently available as cursor for fetching newer logs
    const lastLog = logs[logs.length - 1]
    if (!lastLog) {
      // No local logs available to use as cursor
      return
    }
    
    setIsLoading(true)
    try {
      const result = await fetchLogs({
        ...options,
        cursor: { timestamp: lastLog.timestamp, sequenceId: lastLog.sequenceId },
        direction: 'after',
        limit: 50
      })
      
      if (result.success && result.logs.length > 0) {
        setLogs(prevLogs => {
          // Filter out any logs that already exist to prevent duplicates
          const existingIds = new Set(prevLogs.map(log => log.id))
          const newLogs = result.logs.filter(log => !existingIds.has(log.id))
          
          
          // Server provides logs in correct order - just append newer logs
          return [...prevLogs, ...newLogs]
        })
        
        setHasMore(prev => ({ 
          ...prev, 
          after: result.hasAfter !== false 
        }))
      }
    } catch (error) {
      console.error('Failed to load newer logs:', error)
    } finally {
      setIsLoading(false)
    }
  }, [fetchLogs, isLoading, hasMore.after, logs])

  /**
   * Add new realtime logs (from WebSocket)
   */
  const addRealtimeLog = useCallback((newLog) => {
    // Check if we should auto-scroll BEFORE adding the new log
    const shouldScrollToBottom = scrollRef.current && (() => {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current
      const scrollBottom = scrollHeight - scrollTop - clientHeight
      
      // If we have very few logs (first logs arriving), always scroll to bottom
      if (logs.length < 5) {
        return true
      }
      
      // Otherwise, only scroll if user is at the very bottom
      return scrollBottom <= 5 // Very tight threshold - user must be at the very bottom
    })()
    
    setLogs(prevLogs => {
      // Check for duplicate IDs before adding
      const isDuplicate = prevLogs.some(log => log.id === newLog.id)
      if (isDuplicate) {
        return prevLogs
      }
      
      // Simply append new log - server ensures proper sequencing
      return [...prevLogs, newLog]
    })
    
    // Auto-scroll to bottom if user was at bottom before the new log was added
    // OR if we have very few logs (initial state)
    if (shouldScrollToBottom) {
      // Use requestAnimationFrame to wait for DOM updates
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (scrollRef.current) {
            const { scrollHeight, clientHeight } = scrollRef.current
            const targetScrollTop = scrollHeight - clientHeight
            scrollRef.current.scrollTop = Math.max(0, targetScrollTop)
            
            // Ensure auto-scroll is enabled when scrolling to bottom
            setShouldAutoScroll(true)
          }
        })
      })
    }
  }, [logs.length])

  /**
   * Handle wheel events to prevent page scrolling when message area is at scroll limits
   */
  const handleWheel = useCallback((e) => {
    if (!scrollRef.current) return

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current
    const isAtTop = scrollTop <= 0
    const isAtBottom = scrollTop >= scrollHeight - clientHeight - 1 // Allow 1px tolerance
    
    // Prevent page scroll when trying to scroll up while at top
    if (isAtTop && e.deltaY < 0) {
      e.preventDefault()
      e.stopPropagation()
      return false
    }
    
    // Prevent page scroll when trying to scroll down while at bottom
    if (isAtBottom && e.deltaY > 0) {
      e.preventDefault()
      e.stopPropagation()
      return false
    }
  }, [])

  /**
   * Handle touch events to prevent page scrolling on mobile devices
   */
  const handleTouchStart = useCallback((e) => {
    if (!scrollRef.current) return
    
    // Store initial touch position
    const touch = e.touches[0]
    scrollRef.current.touchStartY = touch.clientY
  }, [])

  const handleTouchMove = useCallback((e) => {
    if (!scrollRef.current || !scrollRef.current.touchStartY) return

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current
    const touch = e.touches[0]
    const touchDeltaY = scrollRef.current.touchStartY - touch.clientY
    
    const isAtTop = scrollTop <= 0
    const isAtBottom = scrollTop >= scrollHeight - clientHeight - 1
    
    // Prevent page scroll when trying to scroll up while at top
    if (isAtTop && touchDeltaY < 0) {
      e.preventDefault()
      e.stopPropagation()
      return false
    }
    
    // Prevent page scroll when trying to scroll down while at bottom
    if (isAtBottom && touchDeltaY > 0) {
      e.preventDefault()
      e.stopPropagation()
      return false
    }
  }, [])

  /**
   * Handle scroll events to detect when to load more logs
   */
  const handleScroll = useCallback(() => {
    if (!scrollRef.current || isLoading || isAdjustingScroll.current) return
    
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current
    const scrollBottom = scrollHeight - scrollTop - clientHeight
    
    // Detect scroll direction
    const isScrollingUp = scrollTop < lastScrollTop.current
    const isScrollingDown = scrollTop > lastScrollTop.current
    lastScrollTop.current = scrollTop
    
    
    // Update auto-scroll flag based on user's scroll position
    const isAtBottom = scrollBottom <= 5 // Very tight threshold for being "at bottom"
    
    // Only update the state if it actually changed
    if (isAtBottom !== shouldAutoScroll) {
      setShouldAutoScroll(isAtBottom)
    }
    
    // Load older logs proactively when user is at 25% from top
    const scrollPercentageFloat = scrollTop / (scrollHeight - clientHeight)
    const isInTopQuarter = scrollPercentageFloat < 0.25
    
    if (isScrollingUp && isInTopQuarter && !isLoading && hasMore.before) {
      loadOlderLogs()
    }
    
    // Load newer logs when scrolling down near bottom (if not in realtime mode)
    if (isScrollingDown && scrollBottom < loadThreshold && hasMore.after && !isLoading) {
      loadNewerLogs()
    }
  }, [isLoading, loadThreshold, loadOlderLogs, loadNewerLogs, hasMore.after, shouldAutoScroll])

  /**
   * Clear all logs (for time range changes)
   */
  const clearLogs = useCallback(() => {
    setLogs([])
    setHasMore({ before: true, after: true })
    setTotalCount(0)
    setShouldAutoScroll(true)
  }, [])

  /**
   * Force scroll to bottom
   */
  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          const { scrollHeight, clientHeight } = scrollRef.current
          const targetScrollTop = Math.max(0, scrollHeight - clientHeight)
          scrollRef.current.scrollTop = targetScrollTop
          setShouldAutoScroll(true)
        }
      })
    }
  }, [])

  return {
    // Data
    logs,
    isLoading,
    totalCount,
    hasMore,
    shouldAutoScroll,
    
    // Actions
    loadInitial,
    loadOlderLogs,
    loadNewerLogs,
    addRealtimeLog,
    clearLogs,
    scrollToBottom,
    
    // Scroll handling
    scrollRef,
    handleScroll,
    handleWheel,
    handleTouchStart,
    handleTouchMove,
    
  }
}

export default useInfiniteScroll